import React from 'react';
import { Calendar, Clock, Briefcase, DollarSign, Moon, Settings, AlarmClock } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета рабочего времени
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Функция форматирования денежных сумм
  const formatMoney = (amount) => {
    return `${formatNumber(amount, 2)} ₽`;
  };

  // Список рабочих дней недели
  const getWorkScheduleDescription = () => {
    if (results.workScheduleType === 'standard') {
      return 'Стандартный (5/2)';
    } else if (results.workScheduleType === 'shift') {
      return `Сменный график ${results.shiftPattern}`;
    } else {
      return 'Произвольный график';
    }
  };

  return (
    <ToolResult title="Результаты расчета рабочего времени">
      {/* Заголовок с периодом */}
      <div className="mb-4 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/30 rounded-lg border border-cyan-100 dark:border-cyan-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-cyan-500" />
          Период: {results.periodName}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          График работы: {getWorkScheduleDescription()}
        </p>
      </div>

      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Рабочие дни и часы */}
        <div className="rounded-lg bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-900/30 dark:to-blue-900/30 p-5 border border-cyan-100 dark:border-cyan-800/50 shadow-sm">
          <h3 className="text-base font-medium text-cyan-700 dark:text-cyan-300 mb-1.5 flex items-center gap-1.5">
            <Clock className="w-4 h-4"/>
            Рабочее время
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-cyan-800 dark:text-cyan-200" aria-live="polite">
              {results.totalWorkingDays} {formatDaysLabel(results.totalWorkingDays)}
            </span>
            <span className="text-lg font-medium text-cyan-600 dark:text-cyan-400">
              ({results.totalWorkingHours} {formatHoursLabel(results.totalWorkingHours)})
            </span>
          </div>
          <p className="text-xs text-cyan-600 dark:text-cyan-400 mt-1">
            Всего дней в периоде: {results.totalDays}, выходных: {results.totalWeekendDays}, праздников: {results.totalHolidays}
          </p>
        </div>

        {/* Зарплата */}
        <div className="rounded-lg bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-5 border border-green-100 dark:border-green-800/50 shadow-sm">
          <h3 className="text-base font-medium text-green-700 dark:text-green-300 mb-1.5 flex items-center gap-1.5">
            <DollarSign className="w-4 h-4"/>
            Заработная плата
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-green-800 dark:text-green-200" aria-live="polite">
              {formatMoney(results.totalWageAmount)}
            </span>
          </div>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            Базовая оплата: {formatMoney(results.baseWageAmount)}, ставка: {results.hourlyRate} ₽/час
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Всего дней в периоде" 
          value={`${results.totalDays} ${formatDaysLabel(results.totalDays)}`} 
          color="blue" 
          icon={<Calendar className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Рабочих дней" 
          value={`${results.totalWorkingDays} ${formatDaysLabel(results.totalWorkingDays)}`} 
          color="cyan" 
          icon={<Briefcase className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Выходных дней" 
          value={`${results.totalWeekendDays} ${formatDaysLabel(results.totalWeekendDays)}`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Праздничных дней" 
          value={`${results.totalHolidays} ${formatDaysLabel(results.totalHolidays)}`} 
          color="orange"
          icon={<Calendar className="w-4 h-4"/>} 
        />

        {results.totalShortenedDays > 0 && (
          <ResultItem 
            label="Сокращенных дней" 
            value={`${results.totalShortenedDays} ${formatDaysLabel(results.totalShortenedDays)}`} 
            color="purple"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}
        
        <ResultItem 
          label="Рабочих часов" 
          value={`${results.totalWorkingHours} ${formatHoursLabel(results.totalWorkingHours)}`}
          color="cyan" 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.totalNightHours > 0 && (
          <ResultItem 
            label="Ночных часов" 
            value={`${results.totalNightHours} ${formatHoursLabel(results.totalNightHours)}`} 
            color="indigo"
            icon={<Moon className="w-4 h-4"/>} 
          />
        )}
        
        {results.overtimeHours > 0 && (
          <ResultItem 
            label="Сверхурочных часов" 
            value={`${results.overtimeHours} ${formatHoursLabel(results.overtimeHours)}`} 
            color="red"
            icon={<AlarmClock className="w-4 h-4"/>} 
          />
        )}
        
        {results.weekendWorkHours > 0 && (
          <ResultItem 
            label="Часов работы в выходные" 
            value={`${results.weekendWorkHours} ${formatHoursLabel(results.weekendWorkHours)}`} 
            color="orange"
            icon={<Calendar className="w-4 h-4"/>} 
          />
        )}
        
        <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
          <ResultItem 
            label="Базовая оплата" 
            value={formatMoney(results.baseWageAmount)} 
            color="green"
            icon={<DollarSign className="w-4 h-4"/>} 
          />
          
          {results.nightWageAmount > 0 && (
            <ResultItem 
              label="Доплата за ночные часы" 
              value={formatMoney(results.nightWageAmount)} 
              color="indigo"
              icon={<Moon className="w-4 h-4"/>} 
            />
          )}
          
          {results.overtimeWageAmount > 0 && (
            <ResultItem 
              label="Доплата за сверхурочные" 
              value={formatMoney(results.overtimeWageAmount)} 
              color="red"
              icon={<AlarmClock className="w-4 h-4"/>} 
            />
          )}
          
          {results.weekendWageAmount > 0 && (
            <ResultItem 
              label="Доплата за работу в выходные" 
              value={formatMoney(results.weekendWageAmount)} 
              color="orange"
              icon={<Calendar className="w-4 h-4"/>} 
            />
          )}
          
          {results.vacationWageAmount > 0 && (
            <ResultItem 
              label="Оплата отпуска" 
              value={formatMoney(results.vacationWageAmount)} 
              color="teal"
              icon={<Calendar className="w-4 h-4"/>} 
            />
          )}
          
          {results.sickLeaveWageAmount > 0 && (
            <ResultItem 
              label="Оплата больничного" 
              value={formatMoney(results.sickLeaveWageAmount)} 
              color="blue"
              icon={<Calendar className="w-4 h-4"/>} 
            />
          )}
          
          <ResultItem 
            label="Общая сумма" 
            value={formatMoney(results.totalWageAmount)} 
            color="green"
            icon={<DollarSign className="w-4 h-4"/>} 
            bold
          />
        </div>
      </div>

      {/* Информация о расчете */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Settings className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Примечание к расчету</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Расчет произведен согласно Трудовому кодексу РФ с учетом всех надбавок за работу 
              в ночное время, сверхурочную работу и работу в выходные/праздничные дни. 
              Для точного расчета заработной платы необходимо учитывать особенности трудового 
              договора и коллективного договора организации.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "день" в зависимости от числа
const formatDaysLabel = (days) => {
  const lastDigit = days % 10;
  const lastTwoDigits = days % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'дней';
  if (lastDigit === 1) return 'день';
  if (lastDigit >= 2 && lastDigit <= 4) return 'дня';
  return 'дней';
};

// Вспомогательная функция для склонения слова "час" в зависимости от числа
const formatHoursLabel = (hours) => {
  const lastDigit = hours % 10;
  const lastTwoDigits = hours % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'часов';
  if (lastDigit === 1) return 'час';
  if (lastDigit >= 2 && lastDigit <= 4) return 'часа';
  return 'часов';
};

export default ResultsOverview;